import { DebtLabel, PaymentLabel, PriceLabel, RenderPaymentStatus, getSubscriptionLabel, getSubscriptionPaymentLabel, renderDate, renderDatetimeInline } from '@/components/features/shared/utils'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Agreement, AgreementStatus, formatAgreementStatus, formatPaymentPlanStatus, getAgreementStatusColor, getPaymentPlanStatusColor } from '@/domain/agreements/agreement'
import { PaymentPlanStatus } from '@/domain/agreements/payment-plan'
import { UserRole } from '@/domain/auth/auth'
import { CollectorAction, getCollectorFullName } from '@/domain/collectors/collector'
import { BillingType } from '@/domain/invoices/invoice'
import { BillingFrequency, ServiceType } from '@/domain/offerings/offering'
import { PaymentData, PaymentSource, PaymentStatus, PaymentTransaction } from '@/domain/payments/payments'
import { InstallmentPeriod, InstallmentPlanInfo } from '@/domain/subscriptions/installments'
import { BillingPeriodData, ModifyBillingPeriodDto, SubscriptionData, SubscriptionStatus, formatSubscriptionStatus, getSubscriptionStatusVariant, isBillingPeriodPending } from '@/domain/subscriptions/subscriptions'
import { useAgreementAction, useDeleteAgreement, useUpdateAgreement } from '@/hooks/api/useAgreements'
import { useCollectors, useModifyPaymentsByCollector } from '@/hooks/api/useCollectors'
import { useAgreementInvoices } from '@/hooks/api/useInvoices'
import { useCreatePaymentPlan, useDeletePaymentPlan, useModifyPaymentPlan } from '@/hooks/api/usePaymentPlans'
import { useAgreementPayments, useCreateMultiPayment, useInvoicePayments, useRefundPayments } from '@/hooks/api/usePayments'
import { useSellers } from '@/hooks/api/useSellers'
import { useCreateCustomSubscriptionPayment, useCreateMultiSubscriptionPayment, useCreateNewBillingPeriod, useCreateNextInstallment, useDeleteBillingPeriod, useDeleteBillingPeriods, useInstallments, useModifyBillingPeriod, useSubscriptions, useUpdatePaymentDescription } from '@/hooks/api/useSubscriptions'
import { AgreementAction } from '@/rest/modules/agreements-api'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import {
  AlertTriangle,
  ArrowRightCircle,
  CalculatorIcon,
  Check,
  ClipboardCheck,
  ClipboardClock,
  DollarSign,
  Info,
  PauseCircle,
  Pencil,
  PlayCircle,
  PlusCircle,
  Receipt,
  RefreshCw,
  Trash
} from 'lucide-react'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { ReturnPaymentModal } from '../../collectors/ReturnPaymentModal'
import { TitleSpan } from '../../shared/TitleSpan'
import { CreateBillingPeriodModal } from '../../subscriptions/CreateBillingPeriodModal'
import { CustomBillingPeriodsModal } from '../../subscriptions/CustomBillingPeriodsModal'
import { DeleteBillingPeriodModal } from '../../subscriptions/DeleteBillingPeriodModal'
import { ModifyBillingPeriodModal } from '../../subscriptions/ModifyBillingPeriodModal'
import { SelectBillingPeriodForModifyModal } from '../../subscriptions/SelectBillingPeriodForModifyModal'
import { SelectBillingPeriodsModal } from '../../subscriptions/SelectBillingPeriodsModal'
import { useAgreementDocumentsManager } from './documents/useAgreementDocumentsManager'
import {
  invalidateAgreementCache,
  invalidateAgreementCacheWithLists,
  invalidateCollectorAndAgreementCache,
} from './hooks/useAgreementCache'
import { useAgreementPaymentActions } from './hooks/useAgreementPaymentActions'
import { ConvertToPaymentPlanModal } from './modals/ConvertToPaymentPlanModal'
import { EditPaymentDescriptionModal } from './modals/EditPaymentDescriptionModal'
import { ModifyAgreementModal } from './modals/ModifyAgreementAmountModal'
import { ModifyPaymentPlanModal } from './modals/ModifyPaymentPlanModal'
import { PaymentDetailModal } from './modals/PaymentDetailModal'
import { SelectCompletedPaymentsForInvoicingModal } from './modals/SelectCompletedPaymentsForInvoicingModal'
import { SelectPendingPaymentsModal } from './modals/SelectPendingPaymentsModal'
import { SelectReceiptsForRefundModal } from './modals/SelectReceiptsForRefundModal'
import { InfoCard } from './sub-components/InfoCard'
import { renderDirectActions, renderInstallmentActions, renderSubscriptionActions } from './sub-components/PaymentActionRenderer'
import { StatusCell } from './sub-components/StatusCell'
import { PaymentRow as PaymentRowType } from './sub-components/PaymentRow'
import { PaymentSummaryBar } from './sub-components/PaymentSummaryBar'
import { PaymentRowsTable } from './sub-components/PaymentTable'

export type PaymentRow = PaymentRowType

interface AgreementPaymentsInfoProps {
  agreement: Agreement
  serviceType?: ServiceType
}

type ModalState =
  | { type: 'createPeriod' }
  | { type: 'multiPayment' }
  | { type: 'customPayment' }
  | { type: 'modifyPlan'; plan: InstallmentPlanInfo }
  | { type: 'returnPayment'; payment: PaymentRow }
  | { type: 'convertToPlan'; payment: PaymentData }
  | { type: 'modifyAmount'; data: PaymentData }
  | { type: 'modifyPeriod'; billingPeriod: BillingPeriodData }
  | { type: 'paymentDetail'; paymentId: string }
  | { type: 'allMultiPayment' }
  | { type: 'periodSelector' }
  | { type: 'deletePeriod' }
  | { type: 'invoicePayments' }
  | { type: 'refundPayments' }
  | { type: 'editPaymentDescription'; payment: PaymentRow; billingPeriodId: string }
  | { type: null }

export const AgreementPaymentsInfo = memo(({
  agreement,
  serviceType,
}: AgreementPaymentsInfoProps) => {
  const navigate = useNavigate()
  const { data: subscriptions = [], isLoading: isLoadingSubs } = useSubscriptions(agreement.id)
  const { data: installments = [], isLoading: isLoadingInstallments } = useInstallments(agreement.id)
  const { data: agreementPayments = [], isLoading: isLoadingPayments } = useAgreementPayments(agreement.id)
  const directPayments = agreementPayments?.filter(p => p.offeringCode !== '99')
  const interestPayments = agreementPayments?.filter(p => p.offeringCode === '99')

  const allPayments: PaymentTransaction[] = [
    ...(subscriptions || []).flatMap(s => s.payments.map(p => p)),
    ...(installments || []).flatMap(i => i.periods?.flatMap(p => p.payments.map(pm => pm)) ?? []),
    ...(agreementPayments || []).flatMap(cp => cp.payments.map(p => p)),
  ].filter((p, i, a) => a.findIndex(x => x.id === p.id) === i)
  const isLoadingAll = isLoadingSubs || isLoadingInstallments || isLoadingPayments

  const { hasAnyRole } = useAuthStore()
  const isAdmin = hasAnyRole([UserRole.Scheduler, UserRole.Admin, UserRole.SuperAdmin])

  const queryClient = useQueryClient()
  const { data: collectors = [] } = useCollectors()
  const { data: sellers = [] } = useSellers()
  const { data: invoices = [] } = useAgreementInvoices(allPayments.map(p => p.id), { enabled: !isLoadingAll })
  const createNewBillingPeriod = useCreateNewBillingPeriod()
  const createMultiPayment = useCreateMultiSubscriptionPayment()
  const createCustomPayment = useCreateCustomSubscriptionPayment()
  const createAllMultiPayment = useCreateMultiPayment()
  const generateNextPayment = useCreateNextInstallment()
  const deletePaymentPlan = useDeletePaymentPlan()
  const modifyPaymentPlan = useModifyPaymentPlan()
  const createPaymentPlan = useCreatePaymentPlan()
  const returnPaymentMutation = useModifyPaymentsByCollector()
  const deleteAgreementMutation = useDeleteAgreement()
  const updateAgreement = useUpdateAgreement()
  const modifySubscriptionAction = useAgreementAction()
  const modifyBillingPeriod = useModifyBillingPeriod()
  const deleteBillingPeriodMutation = useDeleteBillingPeriod()
  const deleteBillingPeriodsMutation = useDeleteBillingPeriods()
  const invoicePaymentsMutation = useInvoicePayments()
  const refundPaymentsMutation = useRefundPayments()
  const updatePaymentDescriptionMutation = useUpdatePaymentDescription()
  const canManageSubscription = hasAnyRole([UserRole.SuperAdmin, UserRole.Admin])

  // console.log(installments, subscriptions, directPayments)
  // console.log(invoices, allPayments)

  const handleCreateBillingPeriod = (subId: string, periodStartDate: string, periodEndDate: string | undefined, amount: number) => {
    createNewBillingPeriod.mutate({ subscriptionId: subId, periodStartDate, periodEndDate, amount }, {
      onSuccess: () => {
        invalidateAgreementCacheWithLists(queryClient, agreement.id)
        closeModal()
      }
    })
  }

  const handleMultiPayment = (subscriptionId: string, billingPeriodIds: string[]) => {
    createMultiPayment.mutate({ subscriptionId, billingPeriodIds }, {
      onSuccess: (response) => {
        invalidateAgreementCache(queryClient, agreement.id)
        closeModal()
        onMultiSubscriptionPaymentRequest({
          subscriptionId,
          paymentId: response.data?.payment?.id
        })
      }
    })
  }

  const handleCustomPayment = (subscriptionId: string, billingPeriodIds: string[], installments: number, installmentAmount: number) => {
    createCustomPayment.mutate({ subscriptionId, billingPeriodIds, installments, installmentAmount }, {
      onSuccess: () => {
        invalidateAgreementCache(queryClient, agreement.id)
        closeModal()
      }
    })
  }

  const handleModifyPaymentPlan = (installments: number, installmentAmount: number) => {
    if (modal?.type !== 'modifyPlan' || !modal.plan?.planId) return
    modifyPaymentPlan.mutate(
      { planId: modal.plan.planId, data: { installments, installmentAmount } },
      {
        onSuccess: () => {
          invalidateAgreementCache(queryClient, agreement.id)
          closeModal()
        }
      }
    )
  }

  const handleConvertToPaymentPlan = (installments: number, installmentAmount: number, initialAmount: number) => {
    if (modal?.type !== 'convertToPlan' || !modal.payment) return
    createPaymentPlan.mutate({
      contractId: modal.payment.contractId,
      installmentAmount,
      installments,
      initialAmount,
    })
    closeModal()
  }

  const handleModifyAgreementAmount = (amount: number) => {
    if (modal?.type !== 'modifyAmount' || !modal.data) return
    updateAgreement.mutate({
      id: modal.data.contractId,
      data: { amount },
    }, {
      onSuccess: () => {
        invalidateAgreementCache(queryClient, agreement.id)
        closeModal()
      }
    })
  }

  const handleReturnPayment = (action: CollectorAction) => {
    if (modal?.type !== 'returnPayment' || !modal.payment || !modal.payment.collectorId) return
    returnPaymentMutation.mutate({
      collectorId: modal.payment.collectorId,
      paymentIds: [modal.payment.id],
      operation: action,
      billingType: BillingType.Receipt,
    }, {
      onSuccess: () => {
        invalidateCollectorAndAgreementCache(queryClient, agreement.id)
        closeModal()
      }
    })
  }

  const handleModifyPeriodAmount = (dto: ModifyBillingPeriodDto) => {
    if (modal?.type !== 'modifyPeriod' || !modal.billingPeriod) return
    modifyBillingPeriod.mutate({
      subscriptionId: modal.billingPeriod.subscriptionId,
      billingPeriodId: dto.billingPeriodId || modal.billingPeriod.id,
      data: dto,
    }, {
      onSuccess: () => {
        closeModal()
        invalidateAgreementCache(queryClient, agreement.id)
      }
    })
  }

  const handleEditPaymentDescription = (description: string | null) => {
    if (modal?.type !== 'editPaymentDescription' || !modal.payment) return
    updatePaymentDescriptionMutation.mutate({
      subscriptionId: modal.payment.__context.referenceId!,
      billingPeriodId: modal.billingPeriodId,
      paymentId: modal.payment.id,
      description,
    }, {
      onSuccess: () => {
        closeModal()
        invalidateAgreementCache(queryClient, agreement.id)
      }
    })
  }

  const handleOpenEditPaymentDescriptionModal = (payment: PaymentRow) => {
    openModal({
      type: 'editPaymentDescription',
      payment,
      billingPeriodId: payment.__context.billingPeriodId!,
    })
  }

  const handleOpenModifyPeriodModal = (sub: SubscriptionData) => {
    isSelectingPeriod.current = true
    openModal({
      type: 'modifyPeriod',
      billingPeriod: {
        id: sub.billingPeriodId,
        subscriptionId: sub.subscriptionId,
        periodStartDate: sub.periodStartDate,
        periodEndDate: sub.periodEndDate,
        dueDate: sub.periodStartDate,
        status: sub.periodStatus,
        totalAmount: sub.totalAmount,
        outstandingAmount: sub.outstandingAmount,
        isPaid: sub.outstandingAmount <= 0,
        billingFrequency: sub.billingFrequency,
      } as BillingPeriodData,
    })
  }

  const handleToggleSubscription = (contractId: string, currentStatus: SubscriptionStatus) => {
    const newStatus = currentStatus === SubscriptionStatus.ACTIVE ? SubscriptionStatus.SUSPENDED : SubscriptionStatus.ACTIVE
    modifySubscriptionAction.mutate({
      contractId,
      action: AgreementAction.ModifySubscription,
      subscriptionStatus: newStatus,
    }, {
      onSuccess: () => {
        invalidateAgreementCacheWithLists(queryClient, agreement.id)
      }
    })
  }

  const handleAllMultiPayment = (paymentIds: string[]) => {
    createAllMultiPayment.mutate({ contractId: agreement.id, paymentIds }, {
      onSuccess: (response) => {
        if (response.data?.payment?.id) {
          navigate({ to: `/${Paths.Management.MultiPayments}/${response.data?.payment?.id}` })
        }
      }
    })
    closeModal()
  }

  const handleDeletePaymentPlan = (planId: string) => {
    if (confirm('¿Está seguro de eliminar el plan de pago? Esta acción no se puede deshacer.')) {
      deletePaymentPlan.mutate(planId)
    }
  }

  const handleDeleteBillingPeriod = (billingPeriods: { subscriptionId: string; billingPeriodId: string }[]) => {
    deleteBillingPeriodsMutation.mutate({ billingPeriods }, {
      onSuccess: () => {
        invalidateAgreementCache(queryClient, agreement.id)
        closeModal()
      }
    })
  }

  const handleDeleteAgreement = (contractId: string) => {
    if (confirm('¿Está seguro de eliminar el cargo? Esta acción no se puede deshacer.')) {
      deleteAgreementMutation.mutate(contractId)
    }
  }

  const handleInvoicePayments = (paymentIds: string[], billingType: BillingType) => {
    invoicePaymentsMutation.mutate({ paymentIds, billingType }, {
      onSuccess: () => {
        closeModal()
      }
    })
  }

  const handleRefundPayments = (paymentIds: string[]) => {
    refundPaymentsMutation.mutate(paymentIds, {
      onSuccess: () => {
        closeModal()
        invalidateAgreementCache(queryClient, agreement.id)
      }
    })
  }

  const getListItemIcon = (isPaid: boolean, inProgress: boolean = false) => {
    return inProgress ? <ClipboardCheck className="h-4 w-4 text-blue-500 flex-shrink-0" /> :
      (isPaid ? <ClipboardCheck className="h-4 w-4 text-success flex-shrink-0" />
        : <ClipboardClock className="h-4 w-4 text-destructive flex-shrink-0" />);
  };

  const canPay = agreement.status === AgreementStatus.Active || agreement.status === AgreementStatus.Pending || agreement.status === AgreementStatus.Terminated

  const {
    onSubscriptionPaymentRequest,
    onPaymentRequest,
    onNewSubscriptionPaymentRequest,
    onMultiSubscriptionPaymentRequest,
    onDeleteDirectPayment,
    onDeletePeriodPayment,
  } = useAgreementPaymentActions(agreement)

  const { onOpenDocument } = useAgreementDocumentsManager(agreement.id)

  const [activeTab, setActiveTab] = useState<string>('todos')
  const [modal, setModal] = useState<ModalState | null>(null)
  const openModal = (state: Exclude<ModalState, null>) => setModal(state)
  const closeModal = () => setModal(null)
  const isSelectingPeriod = useRef(false)
  const hasSubscriptions = (subscriptions?.length > 0)
  const hasInstallments = (installments?.length > 0)
  const hasContractPayments = (directPayments?.length ?? 0) > 0
  const hasInterestPayments = (interestPayments?.length ?? 0) > 0
  const hasTodos = hasSubscriptions || hasInstallments || hasContractPayments || hasInterestPayments

  const [subUniquePeriods, subTotalAmount, subOutstandingAmount, isSubCompleted] = useMemo(() => {
    const unique = Array.from(new Map((subscriptions || []).map(s => [s.billingPeriodId, s])).values())
    const total = unique.reduce((sum, s) => sum + s.totalAmount, 0)
    const outstanding = unique.reduce((sum, s) => sum + s.outstandingAmount, 0)
    const completed = unique.length > 0 && unique.every(s => s.outstandingAmount <= 0)
    return [unique, total, outstanding, completed] as const
  }, [subscriptions])

  const defaultTab = useMemo(() => {
    if (hasTodos) return 'todos'
    if (hasSubscriptions) return 'subscriptions'
    if (hasInstallments) return installments[0].planId || `plan-${0}`
    if (hasContractPayments) return `cp-${directPayments?.[0].contractId}`
    if (hasInterestPayments) return `cp-${interestPayments?.[0].contractId}`
    return 'todos'
  }, [hasTodos, hasSubscriptions, hasInstallments, hasContractPayments, hasInterestPayments, installments, directPayments, interestPayments])

  useEffect(() => {
    setActiveTab(defaultTab)
  }, [defaultTab])

  // --- Mappers ---
  const mapSubscriptionToRows = (sub: SubscriptionData): PaymentRow[] => {
    return sub.payments.map(p => ({
      ...p,
      __source: PaymentSource.Subscription,
      __context: {
        referenceId: sub.subscriptionId,
        offeringCode: sub.offeringCode,
        offeringName: sub.offeringName,
        billingPeriodId: sub.billingPeriodId,
        periodStatus: sub.periodStatus,
        outstandingAmount: sub.outstandingAmount,
        hasPendingPayment: sub.payments.some(i => i.status === PaymentStatus.PENDING),
        amountDebt: sub.totalAmount,
        label: getSubscriptionLabel(sub.periodStartDate, sub.periodEndDate, sub.billingFrequency),
        periodDate: getSubscriptionPaymentLabel(p.paymentPeriodStartDate || sub.periodStartDate, p.description),
        agreementId: agreement.id
      }
    }))
  }

  const mapInstallmentToRow = (p: PaymentTransaction, info: InstallmentPlanInfo, period: InstallmentPeriod): PaymentRow => ({
    ...p,
    __source: PaymentSource.Installment,
    __context: {
      referenceId: info.contractId,
      offeringCode: info.offeringCode,
      offeringName: info.offeringName,
      amountDebt: period.installmentValue,
      outstandingAmount: 0,
      label: period.installmentNumber === 0 ? 'Entrega' : ` Cuota ${period.installmentNumber}/${info.installments}`,
      periodDate: getSubscriptionLabel(period.periodDate, period.periodDate, BillingFrequency.MONTHLY),
      agreementId: agreement.id
    }
  })

  const mapDirectPaymentToRow = (p: PaymentTransaction, data: PaymentData): PaymentRow => ({
    ...p,
    __source: PaymentSource.Direct,
    __context: {
      referenceId: data.contractId,
      offeringCode: data.offeringCode,
      offeringName: data.offeringName,
      amountDebt: data.contractAmount,
      outstandingAmount: data.contractAmount - p.amount,
      label: data.offeringCode === '99' ? 'Interés tarjeta' : (p.amount < data.contractAmount ? 'Pago parcial' : 'Pago total'), //TODO refactor of codes
      periodDate: getSubscriptionLabel(data.contractStartDate, data.contractEndDate ?? data.contractStartDate, BillingFrequency.MONTHLY),
      agreementId: agreement.id
    },
  })

  const computeInstallmentPlanStats = (plan: InstallmentPlanInfo) => {
    const amount = plan.initialAmount + plan.installmentAmount
    const totalPaid = plan.periods.flatMap(p => p.payments)
      .filter(i => i.status === PaymentStatus.COMPLETED)
      .map(i => i.amount).reduce((a, b) => a + b, 0)
    const amountOwed = amount - totalPaid
    const isComplete = amountOwed < 1
    const installmentsGenerated = plan.periods.length - (plan.initialAmount ? 1 : 0) >= plan.installments
    const allPaid = plan.periods.flatMap(p => p.payments).every(i => i.status === PaymentStatus.COMPLETED)
    return { amount, amountOwed, isComplete, installmentsGenerated, allPaid }
  }

  const computeDirectPaymentStats = (data: PaymentData) => {
    const amountPaid = data.payments.filter(p => p.status === PaymentStatus.COMPLETED || p.status === PaymentStatus.IN_COLLECTOR)
      .reduce((sum, payment) => sum + payment.amount, 0)
    const amountOwed = data.contractAmount - amountPaid
    const pendingPaymentId = data.payments.find(p => p.status === PaymentStatus.PENDING)?.id
    return { amountPaid, amountOwed, pendingPaymentId }
  }

  const combinedRows = useMemo<PaymentRow[]>(() => [
    ...(subscriptions || []).flatMap(sub => mapSubscriptionToRows(sub)),
    ...(installments || []).flatMap(plan => plan.periods.flatMap(p => p.payments.map(pm => mapInstallmentToRow(pm, plan, p)))),
    ...(directPayments || []).flatMap(cp => cp.payments.map(p => mapDirectPaymentToRow(p, cp))),
    ...(interestPayments || []).flatMap(cp => cp.payments.map(p => mapDirectPaymentToRow(p, cp))),
  ], [subscriptions, installments, directPayments, interestPayments])

  const completedPayments = useMemo(() => combinedRows.filter(p => p.status === PaymentStatus.COMPLETED).sort((a, b) => b.paymentDate.getTime() - a.paymentDate.getTime()), [combinedRows])
  const sortedCombinedRows = useMemo(() => [...combinedRows].sort((a, b) => b.paymentDate.getTime() - a.paymentDate.getTime()), [combinedRows])

  const renderActionsCell = (row: PaymentRow) => {
    const { referenceId } = row.__context

    if (!referenceId) return <RenderPaymentStatus paymentStatus={row.status} collectorCode={collectors?.find(c => c.id === row.collectorId)?.code} />

    const actionsDeps = {
      invoices: invoices.filter(i => i.paymentId == row.id),
      canPay,
      onSubscriptionPaymentRequest,
      onNewSubscriptionPaymentRequest: (contractId: string, billingPeriodId: string) => onNewSubscriptionPaymentRequest(contractId, billingPeriodId),
      onDeleteDirectPayment,
      onDeletePeriodPayment,
      onPaymentRequest,
      onEditDescription: handleOpenEditPaymentDescriptionModal,
      openModal,
      onOpenDocument,
    }

    if (row.__source === PaymentSource.Subscription) {
      return renderSubscriptionActions(row, actionsDeps)
    }

    if (row.__source === PaymentSource.Installment) {
      return renderInstallmentActions(row, actionsDeps)
    }

    return renderDirectActions(row, actionsDeps)
  }

  const paymentTodosColumns: ColumnDef<PaymentRow>[] = [
    {
      accessorKey: 'offeringCode',
      header: 'Rubro',
      cell: ({ row }) => {
        return <Badge variant={'outline'} className='font-bold'>
          {row.original.__context.offeringCode}
        </Badge>
      },
    },
  ]

  const paymentColumns: ColumnDef<PaymentRow>[] = [
    {
      accessorKey: 'periodStartDate',
      header: 'Concepto',
      cell: ({ row }) => {
        const { label } = row.original.__context;
        if (row.original.__source === PaymentSource.Subscription) {
          return <span className="ml-1 font-semibold font-mono">
            {label}
          </span>
        }
        else if (row.original.__source === PaymentSource.Installment) {
          return <div className="flex flex-col items-start">
            <Badge variant="outline" className="font-mono text-[11px] h-5 px-1.5 w-[max-content]">
              {label}
            </Badge>
          </div>
        }
        else if (row.original.__source === PaymentSource.Direct) {
          return <span className="text-xs text-muted-foreground font-bold leading-none">
            {label}
          </span>
        }
        return '-';
      },
    },
    {
      accessorKey: '__context.amountDebt',
      header: 'Valor',
      cell: ({ row }) => {
        return <PriceLabel price={row.original.__context.amountDebt ?? 0} />;
      },
    },
    {
      accessorKey: '__context.periodDate',
      header: 'Período de pago',
      cell: ({ row }) => {
        const val = row.original.__context.periodDate
        return val ? val : '-'
      },
    },
    {
      accessorKey: 'paymentDate',
      header: 'Fecha de pago',
      cell: ({ row }) => {
        const val = row.original.paymentDate
        return row.original.status === PaymentStatus.COMPLETED ? renderDatetimeInline(val) : "-"
      },
    },
    {
      accessorKey: 'amount',
      header: 'Importe',
      cell: ({ row }) => {
        const interestSum = row.original.interestSum
        const totalAmount = row.original.amount
        // const isRefunded = row.original.status === PaymentStatus.REFUNDED
        const isCompleted = row.original.status === PaymentStatus.COMPLETED || row.original.status === PaymentStatus.IN_COLLECTOR || row.original.status === PaymentStatus.CANCELLED
        return <div className='flex flex-col gap-1'>
          {/* {isRefunded ? <RefundLabel amount={totalAmount} /> */}
          {isCompleted ? <PaymentLabel amount={totalAmount} interest={interestSum > 0} />
            : <DebtLabel amount={totalAmount} />}
        </div>
      },
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => <StatusCell row={row.original} collectors={collectors} />,
    },
    {
      accessorKey: 'actions',
      header: 'Acciones',
      cell: ({ row }) => renderActionsCell(row.original),
    },
  ]

  const handleRowClick = (row: PaymentRow) => {
    openModal({ type: 'paymentDetail', paymentId: row.id })
  }

  const paymentTableProps = {
    setSelectedPaymentId: () => { },
    setShowPaymentModal: () => { },
  }

  const perpetuidad = subscriptions?.[0]?.subscriptionStatus === SubscriptionStatus.CANCELLED && serviceType === ServiceType.PARCELAS

  const tabItems = useMemo(() => [
    ...(hasSubscriptions && subscriptions ? [{
      id: 'subscriptions',
      label: subscriptions.at(0)?.offeringName || '',
      offeringCode: subscriptions.at(0)?.offeringCode || '',
      done: (subscriptions.reduce((sum, s) => sum + s.outstandingAmount, 0) === 0),
      date: subscriptions.at(0)?.subStartDate
    }] : []),
    ...(directPayments?.map((cp: PaymentData) => ({
      id: `cp-${cp.contractId}`,
      label: cp.offeringName,
      offeringCode: cp.offeringCode,
      done: cp.status === AgreementStatus.Active || cp.status === AgreementStatus.Terminated,
      date: cp.contractStartDate
    })) || []),
    ...(installments?.map((plan, index) => ({
      id: plan.planId || `plan-${index}`,
      label: serviceType == ServiceType.ABONOS ? "Anticipo de servicio" : plan.offeringName,
      offeringCode: plan.offeringCode,
      done: plan.planStatus === PaymentPlanStatus.PAYED,
      date: plan.initialPeriodDate
    })) || [])
  ].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date.getTime() - b.date.getTime();
  }), [hasSubscriptions, subscriptions, directPayments, installments, serviceType])

  return (
    <Card className="">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className='text-2xl'>Información de pagos</CardTitle>
          <div className="flex items-center gap-3">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => openModal({ type: 'refundPayments' })}
              disabled={completedPayments.length === 0 || refundPaymentsMutation.isPending}
              title="Reembolsar"
            >
              <RefreshCw className="h-3 w-3" />
              <span className="hidden sm:inline">Reembolsar</span>
            </Button>
            <Button
              variant="warning"
              size="sm"
              onClick={() => openModal({ type: 'invoicePayments' })}
              disabled={completedPayments.length === 0 || invoicePaymentsMutation.isPending}
              title="Facturar"
            >
              <Receipt className="h-3 w-3" />
              <span className="hidden sm:inline">Facturar</span>
            </Button>
            {canPay && combinedRows.some(p => p.status === PaymentStatus.PENDING) && (
              <Button
                variant="info"
                size="sm"
                onClick={() => openModal({ type: 'allMultiPayment' })}
                title="Pago múltiple"
              >
                <DollarSign className="h-3 w-3" />
                <span className="hidden sm:inline">Pago múltiple</span>
              </Button>
            )}
          </div>
        </div>
        <CardDescription>Detalle de pagos del contrato</CardDescription>
      </CardHeader>
      <CardContent className='px-4'>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto w-full justify-start py-0">
            {hasTodos && (
              <TabsTrigger value="todos">
                Todos
              </TabsTrigger>
            )}

            {tabItems.map(item => (
              <TabsTrigger key={item.id} value={item.id} className={`${item.done ? '' : 'bg-red-200'}`}>
                {item.done ? <Check className='w-4 mr-1 shrink-0' /> : <AlertTriangle className='w-4 mr-1 shrink-0' />} {item.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="space-y-8 min-h-[300px]">
            {hasSubscriptions && (
              <TabsContent value="subscriptions" className="space-y-4 animate-in fade-in duration-500 mt-0">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    {getListItemIcon(isSubCompleted)}
                    {subscriptions?.at(0)?.offeringCode} - {subscriptions?.at(0)?.offeringName}
                  </h3>
                  <PaymentSummaryBar totalAmount={subTotalAmount} outstandingAmount={subOutstandingAmount} />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const firstSub = subscriptions?.[0]
                      if (!firstSub) return null
                      return (
                        <div className="flex items-center gap-5">
                          <InfoCard
                            rows={[
                              [
                                <div key="status" className="flex flex-col justify-center gap-1 w-full">
                                  <TitleSpan>Estado</TitleSpan>
                                  <Badge className="w-fit" variant={getSubscriptionStatusVariant(firstSub.subscriptionStatus)}>{formatSubscriptionStatus(firstSub.subscriptionStatus)}</Badge>
                                </div>,
                                <div key="start" className="flex flex-col justify-center gap-1">
                                  <TitleSpan>Inicio</TitleSpan>
                                  <span className="text-sm">{renderDate(firstSub.subStartDate)}</span>
                                </div>,
                                firstSub.subEndDate && (
                                  <div key="end" className="flex flex-col justify-center gap-1">
                                    <TitleSpan>Fin</TitleSpan>
                                    <span className="text-sm">{renderDate(firstSub.subEndDate)}</span>
                                  </div>
                                ),
                              ].filter(Boolean),
                            ]}
                            className="max-w-2xl"
                          />
                          {canManageSubscription && firstSub.subscriptionStatus !== SubscriptionStatus.CANCELLED && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className=" px-1.5 text-[10px] font-normal"
                              onClick={() => handleToggleSubscription(firstSub.contractId, firstSub.subscriptionStatus)}
                              disabled={modifySubscriptionAction.isPending}
                            >
                              {firstSub.subscriptionStatus === SubscriptionStatus.ACTIVE ? (
                                <>
                                  <PauseCircle className="h-3 w-3 mr-0.5" />
                                  Suspender
                                </>
                              ) : (
                                <>
                                  <PlayCircle className="h-3 w-3 mr-0.5" />
                                  Activar
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                  {perpetuidad ? (
                    <div className="flex flex-col gap-4">
                      <Alert variant="info" className="bg-green-50/50 border-green-100">
                        <Info className="h-5 w-5" />
                        <AlertTitle>
                          <div className="flex items-center gap-1.5 font-bold">
                            <span>Expensas a perpetuidad</span>
                          </div>
                        </AlertTitle>
                        <AlertDescription>
                          No se generarán nuevos períodos de expensa anual debido al código 'S' de expensas
                        </AlertDescription>
                      </Alert>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => openModal({ type: 'periodSelector' })}
                        disabled={modifyBillingPeriod.isPending || subscriptions?.length === 0}
                        title="Modificar Período"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="info"
                        size="sm"
                        className="h-8"
                        onClick={() => openModal({ type: 'createPeriod' })}
                        disabled={createNewBillingPeriod.isPending}
                        title="Agregar Período"
                      >
                        <PlusCircle className="h-3 w-3" />
                      </Button>
                      {canPay && subOutstandingAmount > 0 && !isSubCompleted && (
                        <>
                          {serviceType === ServiceType.ABONOS && <Button
                            variant="info"
                            className="h-8"
                            onClick={() => openModal({ type: 'multiPayment' })}
                            disabled={createMultiPayment.isPending}
                            title="Pago múltiple"
                          >
                            <DollarSign className="h-3 w-3" />
                          </Button>}
                          <Button
                            variant="warning"
                            className="h-8"
                            onClick={() => openModal({ type: 'customPayment' })}
                            disabled={createCustomPayment.isPending}
                            title="Generar plan de pago"
                          >
                            <CalculatorIcon className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                      {isAdmin && <Button
                        variant="destructive"
                        size="sm"
                        className="h-8"
                        onClick={() => openModal({ type: 'deletePeriod' })}
                        disabled={deleteBillingPeriodMutation.isPending || subscriptions.every(s => !isBillingPeriodPending(s))}
                        title="Eliminar Período"
                      >
                        <Trash className="h-3 w-3" />
                      </Button>}
                    </div>
                  )}
                </div>
                <div className="grid gap-3">
                  <PaymentRowsTable
                    columns={paymentColumns}
                    rows={(subscriptions || []).flatMap(sub => mapSubscriptionToRows(sub))}
                    onRowClick={handleRowClick}
                    groupKey={(row: PaymentRow) => row.__context.billingPeriodId || row.__context.referenceId || ''}
                    groupCells={['periodStartDate', '__context']}
                    {...paymentTableProps}
                  />
                </div>
              </TabsContent>
            )}

            {installments?.map((plan, index) => {
              const headerName = serviceType == ServiceType.ABONOS
                ? "Anticipo de servicio"
                : plan.offeringCode + ' - ' + plan.offeringName
              const stats = computeInstallmentPlanStats(plan)
              return (
                <TabsContent value={plan.planId || `plan-${index}`} key={plan.planId || index} className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300 mt-0">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h3 className="text-lg font-bold flex items-center gap-2 text-left">
                      {getListItemIcon(stats.allPaid, plan.planStatus === PaymentPlanStatus.ACTIVE)}
                      <span className="truncate">{headerName}</span>
                    </h3>
                    <PaymentSummaryBar totalAmount={stats.amount} outstandingAmount={stats.amountOwed} />
                  </div>
                  <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-2">
                    <InfoCard
                      rows={[
                        [
                          <div key="initial" className="space-y-1">
                            <TitleSpan>Entrega Inicial</TitleSpan>
                            <PriceLabel price={plan.initialAmount}></PriceLabel>
                          </div>,
                          <div key="total" className="space-y-1">
                            <TitleSpan>Monto Total</TitleSpan>
                            <PriceLabel price={stats.amount}></PriceLabel>
                          </div>,
                          <div key="installments" className="space-y-1">
                            <TitleSpan>Cuotas Pagadas</TitleSpan>
                            <p className="text-base font-semibold">{plan.installmentsPaid} / {plan.installments}</p>
                          </div>,
                          <div key="paid" className="space-y-1">
                            <TitleSpan>Monto Abonado</TitleSpan>
                            <PriceLabel price={(stats.amount - stats.amountOwed)}></PriceLabel>
                          </div>,
                          <div key="pending" className="space-y-1">
                            <TitleSpan>Saldo Pendiente</TitleSpan>
                            {stats.amountOwed > 0 ? <DebtLabel amount={stats.amountOwed} /> : <PriceLabel price={0} />}
                          </div>,
                        ],
                        [
                          <div key="status" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Estado</TitleSpan>
                            <Badge variant="outline" className={`w-fit font-medium ${getPaymentPlanStatusColor(plan.planStatus)}`}>{formatPaymentPlanStatus(plan.planStatus)}</Badge>
                          </div>,
                          <div key="start" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Inicio</TitleSpan>
                            <span className="text-sm">{renderDate(plan.initialPeriodDate)}</span>
                          </div>,
                          plan.finalPeriodDate && (
                            <div key="end" className="flex flex-col justify-center gap-1">
                              <TitleSpan>{plan.planStatus === PaymentPlanStatus.PAYED ? 'Finalizado' : 'Vencimiento'}</TitleSpan>
                              <span className="text-sm">{renderDate(plan.finalPeriodDate)}</span>
                            </div>
                          ),
                          <div key="seller" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Vendedor</TitleSpan>
                            <span className="text-xs">{getCollectorFullName(sellers.find(s => s.id === plan.sellerId))}</span>
                          </div>
                        ]
                      ]}
                      className="max-w-3xl"
                    />
                    <div className="flex items-center justify-end gap-3">
                      {canPay && (
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => openModal({ type: 'modifyPlan', plan })}
                          disabled={deletePaymentPlan.isPending}
                          title="Modificar Plan"
                        >
                          <CalculatorIcon className="h-3 w-3" />
                        </Button>
                      )}
                      {canPay && !stats.installmentsGenerated && (
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() => generateNextPayment.mutate({ contractId: agreement.id, planId: plan.planId! })}
                          disabled={generateNextPayment.isPending}
                          title="Generar Cuota"
                        >
                          <PlusCircle className="h-3 w-3" />
                        </Button>
                      )}
                      {canPay && plan.planStatus !== PaymentPlanStatus.PAYED && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeletePaymentPlan(plan.planId!)}
                          disabled={deletePaymentPlan.isPending}
                          title="Eliminar Plan"
                        >
                          <Trash className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <PaymentRowsTable
                    columns={paymentColumns}
                    rows={plan.periods.flatMap(p => ({ payments: [...p.payments], period: p, plan })).flatMap(i => i.payments.map(p => mapInstallmentToRow(p, i.plan, i.period)))}
                    onRowClick={handleRowClick}
                    {...paymentTableProps}
                  />
                </TabsContent>
              );
            })}

            {directPayments?.map((data: PaymentData) => {
              const stats = computeDirectPaymentStats(data)
              return (
                <TabsContent value={`cp-${data.contractId}`} key={`cp-${data.contractId}`} className="space-y-4 animate-in fade-in duration-500 mt-0">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                      {getListItemIcon(data.payments.every(p => p.status === PaymentStatus.COMPLETED))}
                      {data.offeringCode} - {data.offeringName}
                    </h3>
                    <PaymentSummaryBar totalAmount={data.contractAmount} outstandingAmount={stats.amountOwed} />
                  </div>
                  <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-2">
                    <InfoCard
                      rows={[
                        [
                          <div key="total" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Monto Total</TitleSpan>
                            <PriceLabel price={data.contractAmount} />
                          </div>,
                          <div key="paid" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Monto abonado</TitleSpan>
                            <PriceLabel price={stats.amountPaid} />
                          </div>,
                          <div key="balance" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Saldo pendiente</TitleSpan>
                            {stats.amountOwed > 0 ? <DebtLabel amount={stats.amountOwed} /> : <PriceLabel price={stats.amountOwed} />}
                          </div>,
                        ],
                        [
                          <div key="status" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Estado</TitleSpan>
                            <Badge variant="outline" className={`w-fit font-medium ${getAgreementStatusColor(data.status)}`}>{formatAgreementStatus(data.status)}</Badge>
                          </div>,
                          <div key="start" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Inicio</TitleSpan>
                            <span className="text-sm">{renderDate(data.contractStartDate)}</span>
                          </div>,
                          data.contractEndDate && (
                            <div key="end" className="flex flex-col justify-center gap-1">
                              <TitleSpan>Finalizado</TitleSpan>
                              <span className="text-sm">{renderDate(data.contractEndDate)}</span>
                            </div>
                          ),
                          <div key="seller" className="flex flex-col justify-center gap-1">
                            <TitleSpan>Vendedor</TitleSpan>
                            <span className="text-xs">{getCollectorFullName(sellers.find(s => s.id === data.sellerId))}</span>
                          </div>
                          ,
                        ].filter(Boolean)
                      ]}
                      className="max-w-xl"
                    />
                    <div className='flex items-center justify-end gap-3'>
                      {/* {stats.amountOwed > 0 && (
                        <Button variant="info" size="sm" className="w-fit px-6"
                          onClick={() => onPaymentRequest({ contractId: data.contractId, amountOwed: stats.amountOwed, paymentId: stats.pendingPaymentId })}
                          title="Pagar saldo pendiente">
                          <DollarSign className="h-4 w-4" />
                        </Button>
                      )} */}
                      {stats.amountPaid < data.contractAmount && (
                        <Button variant="warning" size="sm" className="w-fit px-6"
                          onClick={() => openModal({ type: 'convertToPlan', payment: data })}
                          title="Crear plan de pago"
                        >
                          <CalculatorIcon className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="w-fit px-4"
                        onClick={() => openModal({ type: 'modifyAmount', data })}
                        title="Modificar monto del contrato"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {stats.amountPaid === 0 && (
                        <Button variant="destructive" size="sm" className="w-fit px-6"
                          onClick={() => handleDeleteAgreement(data.contractId!)}
                          title="Eliminar cargo"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <PaymentRowsTable
                    columns={paymentColumns}
                    rows={(data.payments || []).map(p => mapDirectPaymentToRow(p, data))}
                    onRowClick={handleRowClick}
                    {...paymentTableProps}
                  />
                </TabsContent>
              )
            })}

            {(!hasTodos) && (
              <TabsContent value="todos" className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-2xl mt-0">
                <Receipt className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No se encontraron registros de pagos</p>
              </TabsContent>
            )}

            {hasTodos && (
              <TabsContent value="todos" className="space-y-4 animate-in fade-in duration-500 mt-0">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <ArrowRightCircle className="h-5 w-5 text-primary" />
                    Todos los pagos
                  </h3>
                </div>
                <PaymentRowsTable
                  columns={[...paymentTodosColumns, ...paymentColumns]}
                  rows={sortedCombinedRows}
                  onRowClick={handleRowClick}
                  {...paymentTableProps}
                />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </CardContent>

      {modal?.type === 'createPeriod' && (
        <CreateBillingPeriodModal
          isOpen
          onClose={closeModal}
          subscription={subscriptions?.[0]}
          onCreateAndPay={handleCreateBillingPeriod}
          isPending={createNewBillingPeriod.isPending}
        />
      )}

      {modal?.type === 'multiPayment' && (
        <SelectBillingPeriodsModal
          isOpen
          onClose={closeModal}
          subscriptions={subscriptions || []}
          onPayMultiple={handleMultiPayment}
          isPending={createMultiPayment.isPending}
          onSuccess={() => createMultiPayment.isSuccess && createMultiPayment.reset()}
        />
      )}

      {modal?.type === 'customPayment' && (
        <CustomBillingPeriodsModal
          isOpen
          onClose={closeModal}
          subscriptions={subscriptions || []}
          onCreateCustomPayment={handleCustomPayment}
          isPending={createCustomPayment.isPending}
          onSuccess={() => createCustomPayment.isSuccess && createCustomPayment.reset()}
        />
      )}

      {modal?.type === 'modifyPlan' && (
        <ModifyPaymentPlanModal
          isOpen
          onClose={closeModal}
          paymentPlan={modal.plan}
          onModify={handleModifyPaymentPlan}
          isPending={modifyPaymentPlan.isPending}
          onSuccess={closeModal}
        />
      )}

      {modal?.type === 'paymentDetail' && (() => {
        const selectedPayment = allPayments.find(p => p.id === modal.paymentId)
        if (!selectedPayment) return null
        return (
          <PaymentDetailModal
            isOpen
            onClose={closeModal}
            payment={selectedPayment}
            invoices={invoices.filter(i => i.paymentId === selectedPayment.id)}
            onOpenDocument={onOpenDocument}
          />
        )
      })()}

      {modal?.type === 'returnPayment' && (
        <ReturnPaymentModal
          isOpen
          onClose={closeModal}
          payment={modal.payment}
          invoice={invoices?.find(i => i.paymentId === modal.payment.id) ?? null}
          onConfirm={handleReturnPayment}
          isPending={returnPaymentMutation.isPending}
        />
      )}

      {modal?.type === 'allMultiPayment' && (
        <SelectPendingPaymentsModal
          isOpen
          onClose={closeModal}
          payments={combinedRows}
          onPayMultiple={handleAllMultiPayment}
          isPending={createAllMultiPayment.isPending}
        />
      )}

      {modal?.type === 'periodSelector' && (
        <SelectBillingPeriodForModifyModal
          isOpen
          onClose={() => {
            if (!isSelectingPeriod.current) closeModal()
            isSelectingPeriod.current = false
          }}
          subscriptions={subscriptions || []}
          onSelect={handleOpenModifyPeriodModal}
          isPending={modifyBillingPeriod.isPending}
        />
      )}

      {modal?.type === 'deletePeriod' && (
        <DeleteBillingPeriodModal
          isOpen
          onClose={closeModal}
          subscriptions={subscriptions || []}
          onDelete={handleDeleteBillingPeriod}
          isPending={deleteBillingPeriodsMutation.isPending}
        />
      )}

      {modal?.type === 'modifyPeriod' && (
        <ModifyBillingPeriodModal
          isOpen
          onClose={closeModal}
          billingPeriod={modal.billingPeriod}
          onAdjust={handleModifyPeriodAmount}
          isPending={modifyBillingPeriod.isPending}
        />
      )}

      {modal?.type === 'editPaymentDescription' && (
        <EditPaymentDescriptionModal
          isOpen
          onClose={closeModal}
          description={modal.payment.description ?? null}
          onSave={handleEditPaymentDescription}
          isPending={updatePaymentDescriptionMutation.isPending}
          payment={modal.payment}
        />
      )}

      {modal?.type === 'convertToPlan' && (
        <ConvertToPaymentPlanModal
          isOpen
          onClose={closeModal}
          data={modal.payment}
          isPending={createPaymentPlan.isPending}
          onConvert={handleConvertToPaymentPlan}
        />
      )}

      {modal?.type === 'modifyAmount' && (
        <ModifyAgreementModal
          isOpen
          onClose={closeModal}
          data={modal.data}
          isPending={updateAgreement.isPending}
          onModify={handleModifyAgreementAmount}
        />
      )}

      {modal?.type === 'invoicePayments' && (
        <SelectCompletedPaymentsForInvoicingModal
          isOpen
          onClose={closeModal}
          payments={completedPayments}
          onInvoice={handleInvoicePayments}
          isPending={invoicePaymentsMutation.isPending}
        />
      )}

      {modal?.type === 'refundPayments' && (
        <SelectReceiptsForRefundModal
          isOpen
          onClose={closeModal}
          payments={completedPayments}
          onRefund={handleRefundPayments}
          isPending={refundPaymentsMutation.isPending}
        />
      )}
    </Card>
  )
})
